import { red } from "@ant-design/colors"
import { faPlus, faTrash, faUserCircle } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Col, Input, Row, Typography } from "antd"
import { useContext, useState } from "react"
import { CreateRoleDto, RoleWithPermissions } from "../../../domain/roles/roles"
import { useRemoveRoleMutation, useRolesByTenantQuery, useUpdateRolePermissionsMutation } from "../../../domain/roles/rolesQueries"
import VSpace from "../../common/VSpace"
import TenantsContext from "../../MyTenant/TenantsContext"
import RolePermissionsModal from "./RolePermissionsModal"

const { Text, Title } = Typography

const TenantRolesSection = () => {
  const { state: { tenant, roles }, dispatch, isNewTenant } = useContext(TenantsContext)
  const [roleId, setRoleId] = useState<number | undefined>()
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false)

  const tenantRolesQuery = useRolesByTenantQuery(tenant.id)
  const updateRolePermissionsMutation = useUpdateRolePermissionsMutation()
  const removeRoleMutation = useRemoveRoleMutation()

  const roleOptions = tenantRolesQuery.data || []

  const removeNewRole = (roleIndex: number) => {
    dispatch({
      type: 'SET_NEW_ROLES', 
      payload: roles.map((role, index) =>
        index === roleIndex ? { ...role, removed: !role.removed } : role
      )
    })
  }

  const removeRole = (roleId: number) => {
    removeRoleMutation.mutate(roleId)
    setIsPermissionsModalOpen(false)
  }

  const updateRolePermissions = (permissions: string[]) => {
    const role = roleOptions.find(role => role.id === roleId)
    if (role) {
      const newRolePermissions: RoleWithPermissions = { ...role, permissions }
      updateRolePermissionsMutation.mutate(newRolePermissions)
    }

    setRoleId(undefined)
    setIsPermissionsModalOpen(false)
  }

  if(isNewTenant) {
    return <></>
  }

  return <section>
    <Title level={5}><FontAwesomeIcon icon={faUserCircle} /> Roles</Title>
    <hr />
    <VSpace>
      <Row gutter={[16, 24]}>
        {roleOptions.map((option, index) => (
          <Col span={6} key={index}>
            <VSpace
              style={{ cursor: "pointer" }}
              onClick={() => {
                setRoleId(option.id)
                setIsPermissionsModalOpen(true)
              }}
            >
              <Text>{option.rolename}</Text>
              <Text type="secondary">{option.description}</Text>
            </VSpace>
          </Col>
        ))}
      </Row>
      {
        roles.map((role, index) => (
          <Row key={index} gutter={24}>
            <Col span={11}>
              <Input
                value={role.rolename}
                placeholder="Rolename"
                onChange={(e) => {
                  const newRolesCopy = [...roles]
                  newRolesCopy[index].rolename = e.target.value
                  dispatch({ type: 'SET_NEW_ROLES', payload: newRolesCopy })
                }}
                status={role.removed ? 'error' : ''}
                readOnly={role.removed}
                style={{
                  backgroundColor: role.removed ? red[0] : undefined,
                  color: role.removed ? red[3] : undefined
                }}
              />
              {
                role.rolename.length === 0 && !role.removed && <Text type="danger">Rolename is required</Text>
              }
            </Col>
            <Col span={11}>
              <Input
                value={role.description}
                placeholder="Description"
                maxLength={100}
                onChange={(e) => {
                  const newRolesCopy = [...roles]
                  newRolesCopy[index].description = e.target.value
                  dispatch({ type: 'SET_NEW_ROLES', payload: newRolesCopy })
                }}
                status={role.removed ? 'error' : ''}
                readOnly={role.removed}
                style={{
                  backgroundColor: role.removed ? red[0] : undefined,
                  color: role.removed ? red[3] : undefined
                }}
              />
              {
                role.description.length === 0 && !role.removed && <Text type="danger">Description is required</Text>
              }
            </Col>
            <Col span={2}>
              <FontAwesomeIcon
                icon={faTrash}
                style={{ cursor: 'pointer' }}
                onClick={() => removeNewRole(index)}
                color={role.removed ? red.primary : undefined}
              />
            </Col>
          </Row>
        ))
      }
      <Button
        type="primary"
        icon={<FontAwesomeIcon icon={faPlus} />}
        onClick={() => {
          const newRole: CreateRoleDto = {
            rolename: '',
            description: '',
            removed: false
          }
          dispatch({ type: 'SET_NEW_ROLES', payload: [...roles, newRole] })
        }}
      >
        Add New Role
      </Button>
    </VSpace>
    <RolePermissionsModal
      isOpen={isPermissionsModalOpen}
      onCancel={() => {
        setRoleId(undefined)
        setIsPermissionsModalOpen(false)
      }}
      onOk={updateRolePermissions}
      roleId={roleId || 0}
      onDelete={() => removeRole(roleId || 0)}
    />
  </section>
}

export default TenantRolesSection